import type { GetGradientColorProps, ApplyGradientToGridProps, RGBColorType } from '../types/utils/colors';

function getSmsStatusColor(status: string) {
    switch (status) {
        case 'Передача не удалась':
            return '#f00';
        case 'Успешно передано':
            return '#008000';
        case 'Ожидаем ответ':
        case 'Ожидает отправки':
            return '#00f';
    }
    return '#fff';
}

function getTicketStatusColor(status: number | string) {
    switch (status) {
        case 1:
        case 'Проверить выполнение':
            return '#ff0';
        case 5:
        case 'Проверено':
            return '#ff862e';
        case 2:
        case 'Доработать':
            return '#ffe256';
        case 3:
        case 'Успешно завершено':
        case 4:
        case 'Закрыто за ненадобностью':
            return '#dbdbdb';
    }
    return '#f9fbff';
}

function getAnswerStatusColor(status: string) {
    switch (status) {
        case 'Ответ не поступил':
            return '#ff0800';
        case 'Ответ поступил в срок':
            return '#6f0';
        case 'Ответ поступил в заранее':
            return '#9f9';
        case 'Ответ поступил с опозданием':
            return '#ff0';
    }
    return '#f9fbff';
}

function getTicketPriorityColor(priority: string) {
    switch (priority) {
        case 'В свободное время':
            return '#dbdbdb';
        case 'Важный':
            return '#ff0';
        case 'Критично важный':
            return '#f0aaaa';
        case 'Цель месяца':
            return '#c7ffff';
        case 'На паузе':
            return '#42f471';
    }
    return '#f9fbff';
}

function getTicketPenaltyColor(penalty: string) {
    return parseInt(penalty) !== 0 ? '#f0aaaa' : '#f9fbff';
}

function getTicketDeadlineColor(dtEnd: string | Date) {
    const parsedDate = typeof dtEnd === 'string' ? window.parseDateTime(dtEnd) : dtEnd;
    if (parsedDate === null) {
        return '#ffc0cb';
    }
    return parsedDate < new Date() ? '#ffc0cb' : '#f9fbff';
}

function getTimeSpanColor(data: unknown, invert: boolean = false) {
    if (typeof data === 'string') {
        switch (invert ? !data.includes('-') : data.includes('-')) {
            case true:
                return '#f0aaaa';
            case false:
                return '#42f471';
        }
    }
    return '#f9fbff';
}

/**
 * Яркость канала цвета (0..255) в линейном пространстве по WCAG 2.x.
 * */
function toLinearChannel(channel: number): number {
    const normalized = channel / 255;
    return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

/**
 * Относительная яркость цвета по WCAG 2.x: 0 — чёрный, 1 — белый.
 * */
function getRelativeLuminance(r: number, g: number, b: number): number {
    return 0.2126 * toLinearChannel(r) + 0.7152 * toLinearChannel(g) + 0.0722 * toLinearChannel(b);
}

/**
 * Яркость, при которой контраст с чёрным и белым совпадает (WCAG 2.x).
 * Выше порога — текст должен быть чёрным, ниже — белым.
 * */
const BLACK_TEXT_LUMINANCE_THRESHOLD = 0.179;

function invertColor(hex: string, isContrastingColor: boolean): string {
    if (hex.indexOf('#') === 0) {
        hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }

    if (hex.length !== 6) {
        throw new Error('Invalid HEX color.');
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if (isContrastingColor) {
        // Подбираем цвет текста по относительной яркости фона, чтобы контраст был максимальным:
        // на светлых фонах (красный, жёлтый и т.п.) текст чёрный, белым он остаётся только на тёмных фонах.
        return getRelativeLuminance(r, g, b) > BLACK_TEXT_LUMINANCE_THRESHOLD ? '#000' : '#fff';
    }

    // invert color components
    const redStr = (255 - r).toString(16);
    const greenStr = (255 - g).toString(16);
    const blueStr = (255 - b).toString(16);

    // pad each with zeros and return
    return '#' + window.padZero(redStr) + window.padZero(greenStr) + window.padZero(blueStr);
}

/**
 * Функция для градиентной покраски ячеек в гриде в соответствии с их значением.
 * */
function getGradientColor({ value, min, max, startColor, middleColor, endColor }: GetGradientColorProps) {
    // Нормализуем значение в диапазоне от 0 до 1
    const normalizedValue = (value - min) / (max - min);
    // Усиления контраста цвета
    const enhancedFactor = Math.pow(normalizedValue, 2.4);
    // Интерполяция цвета
    const interpolate = (start: number, end: number, factor: number) => Math.round(start + (end - start) * factor);

    if (enhancedFactor <= 0.5) {
        const subFactor = enhancedFactor * 2;
        const color = startColor.map((start, index) => interpolate(start, middleColor[index], subFactor));
        return `rgb(${color.join(', ')})`;
    }
    else {
        const subFactor = (enhancedFactor - 0.5) * 2;
        const color = middleColor.map((middle, index) => interpolate(middle, endColor[index], subFactor));
        return `rgb(${color.join(', ')})`;
    }
}

function applyGradientToGrid({ instanceGrid }: ApplyGradientToGridProps) {
    if (!instanceGrid) {
        console.error('Grid not found.');
        return;
    }

    const columns = instanceGrid.columns.filter(c => c.attributes?.['data-gradient']).map(({ attributes, field }) => {
        const [start, middle, end] = [
            attributes?.['data-start-color'],
            attributes?.['data-middle-color'],
            attributes?.['data-end-color']
        ];

        if (!attributes?.['data-gradient'] || !start || !middle || !end) {
            console.error(`Could not find gradient colors attribute for column: ${field}.`);
            return {};
        }

        return {
            field,
            startColor: start.split(/,\s+/).map(Number) as RGBColorType,
            middleColor: middle.split(/,\s+/).map(Number) as RGBColorType,
            endColor: end.split(/,\s+/).map(Number) as RGBColorType
        };
    });
    const data = instanceGrid.dataSource.data();

    columns.forEach(({ field, startColor, middleColor, endColor }) => {
        if (!field) {
            console.error(`Column with name ${field} does not exist.`);
            return;
        }

        const values = data.map((item: Record<string, any>) => item[field]);
        const [min, max] = [Math.min(...values), Math.max(...values)];
        const { columnIndex, scope } = window.getColumnIndexWithScopeByField(instanceGrid, field);

        scope.find('.k-master-row').each((_, rowElement) => {
            const row = $(rowElement);
            const dataItem = instanceGrid.dataItem(row) as Record<string, any>;
            const value = dataItem[field];

            if (value !== undefined) {
                const color = getGradientColor({ value, min, max, startColor, middleColor, endColor });
                const cell = row.find(`td:eq(${columnIndex})`);
                cell.css('background-color', color);
            }
        });
    });
}

window.getTimeSpanColor = getTimeSpanColor;
window.getAnswerStatusColor = getAnswerStatusColor;
window.getSmsStatusColor = getSmsStatusColor;
window.getTicketStatusColor = getTicketStatusColor;
window.getTicketDeadlineColor = getTicketDeadlineColor;
window.getTicketPenaltyColor = getTicketPenaltyColor;
window.invertColor = invertColor;
window.getTicketPriorityColor = getTicketPriorityColor;
window.applyGradientToGrid = applyGradientToGrid;